import { useRef, useEffect, useCallback, FC, ReactNode } from 'react'
import { usePrefersReducedMotion } from 'lib/hooks/usePrefersReducedMotion'
import { lerp } from 'lib/helpers'
import { motion } from 'motion/react'
import { useHasTouch } from 'lib/hooks/useHasTouch'
import gsap from 'gsap'

const SPEED = 0.45

type AutoScrollProps = {
  gutter?: number
  className?: string
  children: ReactNode
  direction?: 'left' | 'right'
}

export const AutoScrollCarousel: FC<AutoScrollProps> = ({
  gutter = 0,
  className = 'aspect-[16/8]',
  children,
  direction = 'left',
}) => {
  const reducedMotion = usePrefersReducedMotion()
  const itemHeight = useRef(0)
  const carouselWidth = useRef(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const directionRef = useRef<'left' | 'right'>(direction)
  const touch = useHasTouch()
  const scroll = useRef({
    current: 0,
    target: 0,
  })

  const carouselRef = useCallback((node) => {
    if (node !== null) {
      itemHeight.current = node.getBoundingClientRect().height
      carouselWidth.current = node.getBoundingClientRect().width
    }
  }, [])

  useEffect(() => {
    if (!containerRef.current || typeof window === 'undefined') return
    if (window.innerWidth < 768) return
    const items = containerRef.current.querySelectorAll('[data-carousel-item]')
    let width = items[0].clientWidth + gutter

    const updateWidth = () => {
      width = items[0].clientWidth + gutter
    }

    const animate = () => {
      if (!reducedMotion) {
        scroll.current.target =
          directionRef.current === 'left'
            ? scroll.current.target - SPEED
            : scroll.current.target + SPEED
      }

      scroll.current.current = lerp(
        scroll.current.current,
        scroll.current.target,
        0.06
      )
      gsap.set(items, {
        x: (i) => {
          return i * width + scroll.current.current
        },
        z: 0,
        modifiers: {
          x: (x) => {
            const s = gsap.utils.wrap(
              -width,
              width * (items.length - 1),
              parseFloat(x)
            )
            return `${s}px`
          },
        },
      })
      requestAnimationFrame(animate)
    }

    requestAnimationFrame(animate)

    window.addEventListener('resize', updateWidth)
  }, [])

  return (
    <div
      ref={containerRef}
      className={`relative w-full overflow-hidden ${className}`}
    >
      <div className="scrollbar-hidden inset-0 z-0 h-full w-full snap-x snap-mandatory scroll-px-20 overflow-y-hidden overflow-x-scroll px-20 md:absolute md:snap-none md:overflow-hidden md:overflow-x-hidden md:overscroll-x-none md:px-0">
        <motion.div
          className="inset-0 h-full w-full cursor-grab whitespace-nowrap active:cursor-grabbing md:absolute md:touch-pan-y"
          ref={carouselRef}
          onWheel={(e) => {
            if (touch) return
            if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) return
            scroll.current.target += -1 * (e.deltaX * 0.5)
            if (e.deltaX > 0) {
              directionRef.current = 'left'
            } else {
              directionRef.current = 'right'
            }
          }}
          onPan={(e, { delta, velocity }) => {
            e.preventDefault()
            const velocityX = velocity.x * 0.001
            if (touch) {
              scroll.current.target += delta.x * 4
            } else {
              scroll.current.target += delta.x + velocityX * 10
            }

            if (scroll.current.current > scroll.current.target) {
              directionRef.current = 'left'
            } else {
              directionRef.current = 'right'
            }
          }}
        >
          {children}
        </motion.div>
      </div>
    </div>
  )
}
